import React, { useContext } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { WorkspaceContext } from '@/contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Shield } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  resource?: string;
  ownerOnly?: boolean;
}

export function ProtectedRoute({ children, resource, ownerOnly }: ProtectedRouteProps) {
  const { user } = useAuth();
  const params = useParams();
  const ctx = useContext(WorkspaceContext); // OPTIONAL; may be null if outside provider
  const isWorkspaceRoute = Boolean(params.workspaceId);

  // Must be logged in for any protected route
  if (!user) return <Navigate to="/auth" replace />;

  // Owner check (before workspace context check)
  if (ownerOnly && user.email !== 'hari@billportal.io') {
    return <div className="p-6">Access denied: Owner only.</div>;
  }
  
  // If this is NOT a workspace route OR the provider isn't available yet,
  // fall back to auth-only protection (no resource checks).
  if (!isWorkspaceRoute || !ctx) {
    return <>{children}</>;
  }

  // From here on, we know WorkspaceContext exists
  const { contextStatus, hasModule, isOwner } = ctx;

  // Owner bypass - always return children
  if (isOwner) return <>{children}</>;

  // Show loading while context loads
  if (contextStatus === 'loading') {
    return <div className="p-4">Loading...</div>;
  }

  // If no specific resource required, just return children
  if (!resource) {
    return <>{children}</>;
  }

  // Check module access using unified entitlements
  if (!hasModule(resource)) {
    return (
      <div className="min-h-[400px] flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-destructive" />
            </div>
            <CardTitle className="text-xl">Module Not Enabled</CardTitle>
            <CardDescription>
              The {resource} module is not available in your current plan. Contact your administrator to upgrade.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button variant="outline" className="w-full">
              <Shield className="w-4 h-4 mr-2" />
              Upgrade Plan
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children};</>;
}

export default ProtectedRoute;